// Third-party
import { SortingRule } from 'react-table';

// Redux
import { AsyncResult, Action, ApiAction } from '../../index';
import api from '../../redux-api';

// Utils
import { COMPARISON_ITEM_TYPES } from '../../../utils/constants';
import { PORTFOLIO_TYPES } from '../../../utils/portfolios';
import { LOCALE } from '../../../utils/i18n';

// Types
import { BenchmarkPortfoliosState } from '../types/benchmark-portfolios';
import BenchmarkPortfolio from '@/types/benchmark-portfolio';
import ComparisonItem from '@/types/comparison-item';

// Initial State
const INITIAL_STATE: BenchmarkPortfoliosState = {
  benchmarkPortfolios: [],
};

// Actions
const GET_MY_BENCHMARK_PORTFOLIOS: ApiAction = {
  type: 'connect/benchmark-portfolios/get-my-benchmark-portfolios',
  endpoint: 'benchmark-portfolios/',
};

const GET_BENCHMARK_PORTFOLIO_BY_ID: ApiAction = {
  type: 'connect/benchmark-portfolios/get-by-id',
  endpoint: 'benchmark-portfolios/:id/',
};

export const DELETE_BENCHMARK_PORTFOLIOS: ApiAction = {
  type: 'connect/benchmark-portfolios/delete-many',
  endpoint: 'benchmark-portfolios/',
};

// Reducer
export default function reducer(state: BenchmarkPortfoliosState = INITIAL_STATE, action: Action): BenchmarkPortfoliosState {
  switch (action.type) {
    case `${GET_MY_BENCHMARK_PORTFOLIOS.type}_SUCCESS`: {
      const { portfolios, next } = action.data.data;

      return {
        ...state,
        benchmarkPortfolios: action.params.next ? state.benchmarkPortfolios.concat(portfolios) : portfolios,
        nextBenchmarkPortfoliosToken: next,
      };
    }
    case `${DELETE_BENCHMARK_PORTFOLIOS.type}_SUCCESS`: {
      const ids = action.data.benchmarkPortfolioIds;
      return {
        ...state,
        benchmarkPortfolios: state.benchmarkPortfolios.filter(p => !ids.includes(p._id)),
      };
    }
    default:
      return state;
  }
}

// Action Creators
export const getMyBenchmarkPortfolios =
  (query?: string, nextToken?: string, sorting?: SortingRule[]): AsyncResult<BenchmarkPortfolio[]> =>
  (dispatch, getState) => {
    const { user } = getState().auth;

    if (!user) {
      return Promise.reject();
    }

    const params: any = {};

    if (query) {
      params.query = query;
    }

    if (nextToken) {
      params.next = nextToken;
    }

    if (sorting && sorting.length > 0) {
      params.sort = `${sorting[0].id} ${sorting[0].desc ? 'desc' : 'asc'}`;
    }

    return api.get(GET_MY_BENCHMARK_PORTFOLIOS, params).then((res: { data: any }) => res.data.portfolios);
  };

export const getBenchmarkPortfolioById =
  (id: string, errorWhiteList?: string[]): AsyncResult<BenchmarkPortfolio> =>
  () =>
    api.get(GET_BENCHMARK_PORTFOLIO_BY_ID, { id }, undefined, errorWhiteList).then((res: { data: any }) => res.data);

export const getBenchmarkPortfolio =
  (item: ComparisonItem, errorWhiteList?: string[]): AsyncResult<BenchmarkPortfolio> =>
  dispatch => {
    if (item.portfolioType !== PORTFOLIO_TYPES.CUSTOM) {
      const benchmarkPortfolio = {
        _id: item._id,
        name: item.name[LOCALE.EN_CA],
        holdings: item.holdings.map(holding => ({
          type: COMPARISON_ITEM_TYPES.BENCHMARK,
          id: holding.id,
          name: holding.name || '',
          symbol: holding.symbol || '',
          weight: holding.weight,
          currency: holding.currency || '',
          riskFreeId: holding.riskFreeId,
        })),
        performance: {
          percentGrowth: item.performance && item.performance.percentGrowth,
          risk: {
            standardDeviation: item.performance && item.performance.standardDeviation,
            maximumDrawdown: item.performance && item.performance.maximumDrawdown,
            correlation: item.performance && item.performance.correlation,
          },
        },
        dates: {},
      } as BenchmarkPortfolio;

      return Promise.resolve(benchmarkPortfolio);
    }
    return dispatch(getBenchmarkPortfolioById(item._id, errorWhiteList));
  };

export const deleteBenchmarkPortfolios = (benchmarkPortfolios: BenchmarkPortfolio[]): AsyncResult<{ benchmarkPortfolioIds: string[] }> => {
  const params = {
    benchmarkPortfolioIds: benchmarkPortfolios.map(benchmark => benchmark._id).join(';'),
  };

  return dispatch => api.delete(DELETE_BENCHMARK_PORTFOLIOS, params);
};
